import * as React from 'react';
import { Input as InputPrimitive } from '@base-ui/react/input';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/ui/lib/utils';

// 文字サイズは両 variant とも 16px。16px 未満は iOS Safari がフォーカス時に
// 自動ズームするため（ズーム抑制解除後に問題になる）。
// disabled はどちらのデザインにも定義がないため、muted トークンによる最小限の表現。
// InputGroupInput（inputGroup.tsx）は同じ variant 接頭辞のリセットで default の見た目を
// 打ち消している。default の状態クラスの接頭辞を変えたら、そちらのリセットも直すこと。
const inputVariants = cva(
  cn(
    'tw:box-border tw:w-full tw:min-w-0 tw:rounded-md tw:bg-background tw:px-3 tw:text-base tw:text-foreground tw:transition-colors tw:outline-none',
    'tw:placeholder:text-input-placeholder',
    'tw:disabled:pointer-events-none tw:disabled:border-muted tw:disabled:bg-muted tw:disabled:text-muted-foreground',
  ),
  {
    variants: {
      // default: FDN UI ガイドラインの input 定義。状態はすべて CSS で表現する
      // - 未入力（placeholder 表示中かつ非フォーカス）: 色付き背景 + FDN Blue 枠線
      //   ※ placeholder を渡さない input は常に「入力済み」の見た目になる
      // - 入力済み: 白背景 + 薄い枠線 / フォーカス中: 白背景 + FDN Blue 枠線
      // - エラー（aria-invalid）: ガイドライン通り枠線なしの赤背景
      //   （色のみの表現になるため、必ず可視のエラーメッセージと併用すること）
      // 未入力・エラーの強調には not-disabled を付け、disabled の見た目が
      // disabled: 系の指定だけで決まるようにする（強調系の方が詳細度で勝つため）
      // plain: マイページ系フォームの現行 Bootstrap input に合わせた控えめな表現
      // - 高さ 40px・1px 枠線で、未入力状態の強調を持たない
      // - エラーは destructive 枠線で示す。現行 Bootstrap の $danger は #F44336 だが、
      //   エラー色を DS で 1 色に保つため --destructive（#c72525）に寄せている
      // - フォーカスは現行 Bootstrap の「明るい青枠 + ぼかしリング」に合わせ、
      //   primary 枠線 + primary/25 リングで表現する（現行の枠線色 #69D0FF は
      //   Bootstrap の算出色でトークンにないため primary に寄せている）
      variant: {
        default: cn(
          'tw:h-12 tw:border-2 tw:border-input-filled-border',
          'tw:not-focus:not-disabled:placeholder-shown:border-input-empty-border tw:not-focus:not-disabled:placeholder-shown:bg-input-empty-background',
          'tw:focus:border-ring tw:focus:bg-background',
          'tw:aria-invalid:not-focus:not-disabled:border-transparent tw:aria-invalid:not-focus:not-disabled:bg-input-error-background tw:aria-invalid:not-focus:not-disabled:placeholder-shown:border-transparent tw:aria-invalid:not-focus:not-disabled:placeholder-shown:bg-input-error-background',
        ),
        plain: cn(
          'tw:h-10 tw:border tw:border-form-border',
          'tw:focus:border-primary tw:focus:ring-3 tw:focus:ring-primary/25',
          'tw:aria-invalid:not-focus:not-disabled:border-destructive',
          // 現行 Bootstrap の disabled は背景だけ変えて枠線を残すため、それに合わせる
          'tw:disabled:border-form-border',
        ),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type InputProps = InputPrimitive.Props & VariantProps<typeof inputVariants>;

const Input = React.forwardRef<HTMLElement, InputProps>(
  ({ className, variant, ...props }, ref) => (
    <InputPrimitive
      ref={ref}
      data-slot="input"
      className={
        typeof className === 'function'
          ? (state: InputPrimitive.State) =>
              cn(inputVariants({ variant }), className(state))
          : cn(inputVariants({ variant }), className)
      }
      {...props}
    />
  ),
);

Input.displayName = 'Input';

export { Input, inputVariants };
